import { useState, type FormEvent } from "react";
import { Link, useLocation, useSearchParams } from "wouter";
import { AuthError, resetPassword } from "../lib/auth";
import { useT } from "../lib/i18n";
import { Field, TextInput } from "../components/ui";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export function ResetPasswordPage() {
  const { t } = useT();
  const [, navigate] = useLocation();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [invalidToken, setInvalidToken] = useState(false);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (password.length < 8) {
      setError(t("register.tooShort"));
      return;
    }
    if (password !== confirm) {
      setError(t("reset.mismatch"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await resetPassword(token!, password);
      navigate("/profile");
    } catch (err) {
      if (err instanceof AuthError && err.status === 401) setInvalidToken(true);
      else setError(err instanceof Error ? err.message : t("reset.failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-sm px-4 py-10 sm:py-16">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{t("reset.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          {!token || invalidToken ? (
            <div className="space-y-4">
              <p className="text-sm text-destructive">{t("reset.invalid")}</p>
              <p className="text-sm text-muted-foreground">
                <Link
                  href="/forgot"
                  className="font-medium text-primary transition-colors hover:underline"
                >
                  {t("reset.requestNew")}
                </Link>
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <Field label={t("reset.newPassword")}>
                <TextInput
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Field>
              <Field label={t("reset.confirmPassword")}>
                <TextInput
                  type="password"
                  required
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
              </Field>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" disabled={busy} className="w-full">
                {busy ? t("reset.busy") : t("reset.submit")}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
